<template>
	<div
		class="nav-mobile-child"
		:class="{'child-is-open': openItemId !== null}"
	>
		<component
			:is="titleLink ? 'a' : 'div'"
			v-if="title"
			:href="titleLink"
			class="nav-mobile-child__title"
			:tabindex="openItemId === null ? null : -1"
		>
			{{ title }}
			<V2Icon
				v-if="titleLink"
				icon-name="right-arrow"
				size="xs"
			/>
		</component>
		<template v-for="item in subNavItems">
			<component
				:is="getItemElement(item)"
				:key="`item-${item.id}`"
				:type="hasChildren(item) ? 'button' : null"
				:href="hasChildren(item) ? null : item.link"
				:target="item.link && !hasChildren(item) ? (mixinIsExternalLink(item.link) ? '_blank' : '_self') : null"
				:rel="item.link && !hasChildren(item) && mixinIsExternalLink(item.link) ? 'noopener' : null"
				class="nav-mobile-child__item"
				:class="{'is-active': item.isActive}"
				:aria-expanded="hasChildren(item) ? (item.id === openItemId ? 'true' : 'false') : null"
				:aria-controls="hasChildren(item) ? getChildId(item) : null"
				:aria-current="!hasChildren(item) && item.isActive ? 'page' : null"
				:tabindex="openItemId === null ? null : -1"
				@click="(event) => openChild(event, item)"
			>
				<span>{{ item.title }}</span>
				<V2Icon
					v-if="!hasChildren(item) && mixinIsExternalLink(item.link)"
					icon-name="launch"
					size="xxs"
				/>
				<V2Icon
					v-if="hasChildren(item)"
					icon-name="right-arrow"
					size="md"
				/>
			</component>
			<NavMobileChild
				v-if="hasChildren(item)"
				:id="getChildId(item)"
				:key="`child-${item.id}`"
				ref="navMobileChild"
				class="-child"
				:class="{'is-open': item.id === openItemId}"
				:aria-hidden="item.id !== openItemId"
				:inert="item.id === openItemId ? null : ''"
				:title="item.title"
				:title-link="item.link"
				:sub-nav-items="item.children"
				@open-child="(itemId) => $emit('open-child', itemId)"
			/>
		</template>
	</div>
</template>

<script>
import V2Icon from '@shared/components/Icon';

export default {
	name:       'NavMobileChild',
	components: { V2Icon },
	props:      {
		subNavItems: {
			type:    Array,
			default: () => [],
		},
		title: {
			type:    String,
			default: null,
		},
		titleLink: {
			type:    String,
			default: null,
		},
	},
	data() {
		return {
			openItemId: null,
		};
	},
	mounted() {
		window.eventBus.$on('mobileNav:close-child', (data) => {
			if (data.id === this.openItemId) {
				this.openItemId = null;
			}
		});
	},
	methods: {
		hasChildren(item) {
			return Boolean(item.children && item.children.length > 0);
		},
		getItemElement(item) {
			if (this.hasChildren(item)) {
				return 'button';
			}

			return item.link ? 'a' : 'span';
		},
		getChildId(item) {
			return `mobile-nav-child-${item.id}`;
		},
		openChild(event, item) {
			if (this.hasChildren(item) && (this.openItemId !== item.id)) {
				this.openItemId = item.id;
				this.$emit('open-child', item.id);
				event.preventDefault();
				event.stopPropagation();
				this.$nextTick(() => {
					document.getElementById(this.getChildId(item))?.querySelector('a[href], button')?.focus();
				});
			}
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@sass/variables';

.nav-mobile-child {
	overflow-x: hidden;
	inset: 0;

	&.-child {
		position: fixed;
		left: 0;
		top: $sp7-5;
		width: 100%;
		height: calc(100vh - #{$sp7-5});
		clip-path: inset(0 0 0 100%);
		transform: translateX(0%);
		transition: transform $trans-time-fast, clip-path $trans-time-fast;
		background-color: var(--color-black);
		pointer-events: none;
		overflow-y: scroll;
		overscroll-behavior: contain;
		-webkit-overflow-scrolling: touch;
		padding-bottom: 10vh;

		.nav-mobile-child.-child {
			top: 0;
		}

		&.is-open {
			clip-path: inset(0);
			transform: translateX(0%);
			pointer-events: auto;
		}

		body.-t-light & { // stylelint-disable-line selector-max-type, selector-no-qualifying-type
			background-color: var(--color-white);
		}
	}

	&.child-is-open {
		overflow-y: hidden;
	}
}

.nav-mobile-child__title {
	@include font(primary, bold, normal, fs-150);
	display: flex;
	align-items: center;
	gap: $sp3;
	margin-left: $sp4;
	margin-right: $sp4;
	margin-bottom: $sp4;
	padding: $sp4 0;
}

.nav-mobile-child__item {
	display: flex;
	justify-content: space-between;
	width: calc(100% - #{$sp4 * 2});
	margin: 0 $sp4;
	padding: $sp4 0;
	border: 0;
	background-color: transparent;
	color: inherit;
	text-align: left;
	font-size: 16px; // stylelint-disable-line property-disallowed-list, sh-waqar/declaration-use-variable

	&:not(:last-child) {
		border-bottom: 1px solid var(--color-grey-900);
	}
}
</style>
